import { AccessTypeTag } from "@/common/commonComponents";
import { Autocomplete2RenderItemFn } from "@/components-v2/Autocomplete2";
import { Box, Container, VBox } from "@/components-v2/Box";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { Icon } from "@/components-v2/Icon";
import { KbdSequence } from "@/components-v2/Kbd";
import { QuickFilterBar } from "@/components-v2/QuickFilterBar";
import { SpotlightDialog } from "@/components-v2/SpotlightDialog";
import { Tabs } from "@/components-v2/Tabs";
import { TextInput } from "@/components-v2/TextInput";
import { IconName } from "@/components-v2/iconMapping";
import { getSelectedDecisionLabel } from "@/dbot/projectList/projectListDomain";
import { useTranslation } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { preventDefault } from "@/utils/events";
import { when } from "@/utils/functions";
import { isMac, keyLabels } from "@/utils/keyboard";
import { handleEnter } from "@/utils/keyboardEvents";
import { observer } from "mobx-react";
import { FC, ReactNode, useMemo } from "react";
import styles from "./portfolioSearchComponents.module.css";
import {
  activeQuickSwitcherTab,
  activeRecentTab,
  PortfolioSearchContext,
  PortfolioSearchState,
  prepQuickFilterBar,
  prepQuickSwitcherTabs,
  prepRecentTabs,
  QUICK_SWITCHER_SHORTCUT_KEY,
  QuickSwitcherContext,
  QuickSwitcherItem,
  quickSwitcherItems,
  quickSwitcherItemsInTab,
  quickSwitcherItemText,
  QuickSwitcherItemType,
  RecentPanelItem,
  recentPanelItemKey,
  recentPanelItems,
  selectQuickSwitcherItem,
  selectRecentPanelItem,
  setQuickSwitcherOpen,
  showRecentPanel,
} from "./portfolioSearchDomain";

export type PortfolioSearchViewProps = {
  state: PortfolioSearchState | undefined;
  context: PortfolioSearchContext;
};

type PortfolioSearchPanelProps = PortfolioSearchViewProps & {
  items: QuickSwitcherItem[];
};

const quickSwitcherIcons: Record<QuickSwitcherItemType, IconName> = {
  company: "buildings",
  contact: "user",
};

type ItemRowProps = {
  icon: IconName;
  name: string;
  tag?: ReactNode;
  subtitle?: string;
  timestamp?: string;
  active: boolean;
  star?: boolean;
};

const ItemRow: FC<ItemRowProps> = observer(
  ({ icon, name, tag, subtitle, timestamp, active, star }) => {
    const { f } = useTranslation();
    return (
      <Box
        paddingX={"sm"}
        paddingY={"md"}
        borderBottom
        hoverBg
        bg={active ? "alt2" : "default"}
        alignItems={"center"}
        className={classNames("color-default", "cursor-pointer", "user-select-none", "truncate")}
        justifyContent={"space-between"}
      >
        <Box alignItems={"center"} gap={"lg"} grow className="truncate">
          {star && <Icon name={"star"} weight={"fill"} color={"accent3"} />}
          <Icon name={icon} />
          <VBox gap={0} className="truncate">
            <div className="truncate">{name}</div>
            {subtitle && <small className={"subtle"}>{subtitle}</small>}
          </VBox>
        </Box>
        <Box>
          {tag}
          {when(timestamp, (ts) => (
            <div className="subtle text-right whitespace-nowrap">
              {f.formatDateTime(ts, { relative: true })}
            </div>
          ))}
        </Box>
      </Box>
    );
  },
);

type QuickSwitcherItemRowProps = {
  item: QuickSwitcherItem;
  active: boolean;
  star?: boolean;
};

const QuickSwitcherItemRow: FC<QuickSwitcherItemRowProps> = observer(({ item, active, star }) => {
  const { t } = useTranslation();
  const labels = item.portfolioLabels.map((label) => t(label)).join(", ");
  return (
    <ItemRow
      icon={quickSwitcherIcons[item.type]}
      name={item.name}
      tag={
        item.type === "company" ? (
          <AccessTypeTag accessType={item.access.accessType} customRole={item.customRole} />
        ) : undefined
      }
      subtitle={labels || undefined}
      timestamp={item.type === "company" ? item.lastVisitedAt : undefined}
      active={active}
      star={star}
    />
  );
});

const renderQuickSwitcherItem: Autocomplete2RenderItemFn<QuickSwitcherItem> = (
  item,
  _queryTokens,
  state,
) => <QuickSwitcherItemRow item={item} active={state.highlighted} />;

const QuickSwitcherShortcut: FC = () => (
  <div className="mr-2">
    <KbdSequence
      keys={[isMac ? keyLabels.cmd : keyLabels.ctrl, QUICK_SWITCHER_SHORTCUT_KEY.toUpperCase()]}
    />
  </div>
);

type QuickSwitcherTriggerProps = {
  placeholder: string;
  onOpen: () => void;
};

const QuickSwitcherTrigger: FC<QuickSwitcherTriggerProps> = ({ placeholder, onOpen }) => (
  <TextInput
    icon={"magnifying-glass"}
    placeholder={placeholder}
    addon={<QuickSwitcherShortcut />}
    readOnly
    inputProps={{
      className: "cursor-pointer",
      onClick: onOpen,
      onKeyDown: handleEnter(onOpen),
    }}
  />
);

type QuickSwitcherTabsProps = {
  state: PortfolioSearchState | undefined;
  items: QuickSwitcherItem[];
};

const QuickSwitcherTabs: FC<QuickSwitcherTabsProps> = observer(({ state, items }) => (
  <div onMouseDown={preventDefault()}>
    <Tabs {...prepQuickSwitcherTabs(state, items)} />
  </div>
));

const QuickSwitcherPanel: FC<PortfolioSearchViewProps> = observer(({ context }) => {
  const { t } = useTranslation();
  return (
    <VBox padding>
      <VBox rounded paddingY={"lg"} className={styles.bg}>
        <Container maxWidth={"md"} gap={"lg"} marginY={"lg"}>
          <h1 className="text-h3" data-testid={"portfolio-header"}>
            {t("portfolio.search.heading")}
          </h1>
          <div>{t("portfolio.search.description")}</div>
          <QuickSwitcherTrigger
            placeholder={t("portfolio.search.placeholder")}
            onOpen={() => context.dispatch([setQuickSwitcherOpen(true)])}
          />
        </Container>
      </VBox>
    </VBox>
  );
});

type QuickSwitcherDialogProps = {
  state: PortfolioSearchState | undefined;
  context: QuickSwitcherContext;
  items: QuickSwitcherItem[];
};

export const QuickSwitcherDialog: FC<QuickSwitcherDialogProps> = observer(
  ({ state, context, items }) => {
    const { t } = useTranslation();
    return (
      <SpotlightDialog
        open={state?.isQuickSwitcherOpen ?? false}
        onOpenChange={(open) => context.dispatch([setQuickSwitcherOpen(open)])}
        title={{ key: "portfolio.search.heading" }}
        items={quickSwitcherItemsInTab(items, activeQuickSwitcherTab(state, items))}
        itemTextFn={(item) => item.name}
        itemSearchTextFn={(item) => quickSwitcherItemText(item, context.t, context.language)}
        renderItemFn={renderQuickSwitcherItem}
        header={<QuickSwitcherTabs state={state} items={items} />}
        emptyMessage={t("noOptions")}
        inputProps={{ icon: "magnifying-glass", placeholder: t("portfolio.search.placeholder") }}
        onSelect={(item) =>
          context.dispatch(selectQuickSwitcherItem(item, state?.overview, context.portfolio))
        }
      />
    );
  },
);

const QuickAccessPanel: FC<PortfolioSearchViewProps> = observer(({ state }) => {
  const { t } = useTranslation();
  return (
    <Container maxWidth={"md"}>
      <div>{t("portfolio.search.quick-access.heading")}</div>
      <QuickFilterBar {...prepQuickFilterBar(state)} />
    </Container>
  );
});

type RecentItemRowProps = {
  item: RecentPanelItem;
  star: boolean;
};

const RecentItemRow: FC<RecentItemRowProps> = observer(({ item, star }) => {
  const i18n = useTranslation();
  switch (item.type) {
    case "company":
      return <QuickSwitcherItemRow item={item} active={false} star={star} />;
    case "project":
      return (
        <ItemRow
          icon={"folder"}
          name={item.name}
          subtitle={item.companyName}
          timestamp={item.updatedAt}
          active={false}
        />
      );
    case "matter":
      return (
        <ItemRow
          icon={"gavel"}
          name={getSelectedDecisionLabel(i18n, item)}
          subtitle={`${item.projectName}, ${item.companyName}`}
          timestamp={item.updatedAt}
          active={false}
        />
      );
  }
});

type RecentRowProps = RecentItemRowProps & {
  onSelect: () => void;
};

const RecentRow: FC<RecentRowProps> = observer(({ item, star, onSelect }) => (
  <button type={"button"} className={"w-full text-left"} onClick={onSelect}>
    <RecentItemRow item={item} star={star} />
  </button>
));

const RecentPanel: FC<PortfolioSearchPanelProps> = observer(({ state, context, items }) => {
  const { t } = useTranslation();
  if (!showRecentPanel(state, items)) return null;
  return (
    <Container maxWidth={"md"}>
      <div>{t("portfolio.search.recent.heading")}</div>
      <Tabs {...prepRecentTabs(state, items)} />
      <VBox gap={0}>
        {recentPanelItems(state, items).map((item) => (
          <RecentRow
            key={recentPanelItemKey(item)}
            item={item}
            star={activeRecentTab(state, items) === "starred"}
            onSelect={() =>
              context.dispatch(selectRecentPanelItem(item, state?.overview, context.portfolio))
            }
          />
        ))}
      </VBox>
    </Container>
  );
});

export const PortfolioSearchView: FC<PortfolioSearchViewProps> = observer(({ state, context }) => {
  const items = useMemo(
    () => quickSwitcherItems(state?.overview, context.portfolio),
    [state?.overview, context.portfolio],
  );
  return (
    <FullScreenLayout
      appBar={context.appBar}
      alertStore={context.alertStore}
      disableAppBarShadow
      disableAppBarBorder
      centerPanelContainerProps={{
        bg: true,
        gap: false,
        className: "gap-16",
      }}
      centerPanel={
        state?.isLoading ? (
          "loader"
        ) : (
          <>
            <QuickSwitcherPanel state={state} context={context} />
            <QuickAccessPanel state={state} context={context} />
            <RecentPanel state={state} context={context} items={items} />
          </>
        )
      }
    />
  );
});
